// Browser-side image compression to keep uploads small (~300-500 KB) without
// pulling in browser-image-compression's whole dep tree. Uses Canvas.

export type CompressOpts = {
  maxDim?: number;
  quality?: number;
  mimeType?: string;
};

export async function compressImage(file: File | Blob, opts: CompressOpts = {}): Promise<Blob> {
  const { maxDim = 1280, quality = 0.82, mimeType = 'image/jpeg' } = opts;
  const img = await blobToImage(file);
  const { width, height } = scale(img.width, img.height, maxDim);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d unsupported');
  ctx.drawImage(img, 0, 0, width, height);

  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('compress failed'))),
      mimeType,
      quality,
    ),
  );
  return blob;
}

function scale(w: number, h: number, max: number) {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w >= h ? max / w : max / h;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  });
}

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export async function uploadImage(
  endpoint: string,
  file: Blob,
  filename: string,
  accessToken: string | null,
): Promise<unknown> {
  const fd = new FormData();
  fd.append('file', file, filename);
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    body: fd,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const msg = data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

export async function uploadDocument(
  endpoint: string,
  file: File,
  amount: number,
  accessToken: string | null,
): Promise<unknown> {
  const fd = new FormData();
  fd.append('amount', String(amount));
  fd.append('file', file, file.name);
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    body: fd,
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const msg = data && typeof data === 'object' && 'error' in data ? String((data as { error: unknown }).error) : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}
